import { ActivatedRouteSnapshot, Router, RouterStateSnapshot, UrlTree } from '@angular/router';
import { Observable, of } from 'rxjs';
import { Store } from '@ngrx/store';
import * as fromApp from '../store/app.reducer';
import { switchMap, timeout, catchError, filter, first } from 'rxjs/operators';
import { inject } from '@angular/core';
import * as AuthActions from '../auth/store/auth.actions';

export const preventLoggedInGuard = (
    route: ActivatedRouteSnapshot,
    state: RouterStateSnapshot
): Observable<boolean | UrlTree> => {
    const store = inject(Store<fromApp.AppState>);
    const router = inject(Router);

    // Allow access to accept-invite page even if logged in
    if (state.url.includes('/accept-invite')) {
        return of(true);
    }

    const hasJwt = !!localStorage.getItem('jwt');

    return store.select(state => state.auth).pipe(
        // Wait for auth to be checked or for a valid auth state
        filter(authState => authState?.authChecked || !!(authState?.employee || authState?.customer)),
        first(),
        switchMap(authState => {
            const isAuth = !!(authState?.employee || authState?.customer);
            // If we have JWT but no auth state and auth hasn't been checked, trigger autologin
            if (hasJwt && !isAuth && !authState?.authChecked) {
                store.dispatch(new AuthActions.Autologin());

                return store.select(state => state.auth).pipe(
                    filter(newAuthState => newAuthState?.authChecked),
                    first(),
                    timeout(60000), // 60 second timeout - avoid redirect on slow networks
                    switchMap(finalAuthState => {
                        const finalIsAuth = !!(finalAuthState?.employee || finalAuthState?.customer);
                        if (finalIsAuth) {
                            return handleAuthenticatedUser(finalAuthState, router);
                        } else {
                            return handleUnauthenticatedUser(state.url);
                        }
                    }),
                    catchError(() => {
                        // On timeout or error, allow access to auth pages
                        return handleUnauthenticatedUser(state.url);
                    })
                );
            }

            if (isAuth) {
                return handleAuthenticatedUser(authState, router);
            } else {
                return handleUnauthenticatedUser(state.url);
            }
        })
    );
};

function handleAuthenticatedUser(authState: any, router: Router): Observable<boolean | UrlTree> {
    // User is authenticated: always go to main screen (assignments). Onboarding is skipped.
    return of(router.createUrlTree(['/components/assignments']));
}

function handleUnauthenticatedUser(url: string): Observable<boolean | UrlTree> {
    // User is not authenticated, allow access to auth pages
    if (isAuthRelatedPage(url)) {
        return of(true);
    }
    // For other routes, this guard shouldn't be applied, but return true anyway
    return of(true);
}

// Helper function to check if the URL is auth-related
function isAuthRelatedPage(url: string): boolean {
    const authPages = ['/', '/download', '/login', '/register', '/forgot-password', '/reset-password-link', '/reset-password', '/password-reset-success', '/accept-invite'];
    return authPages.some(page => url.includes(page));
}